import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Plane, Loader2, AlertTriangle, Search } from 'lucide-react';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function AdminFlights() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  const [data, setData] = useState(null);
  const [insp, setInsp] = useState(null);
  const [route, setRoute] = useState({ origin: 'CCU', destination: 'DEL', date: '2026-09-15' });

  useEffect(() => { document.title = 'Flight Comparison | Admin'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    api.adminFlightComparison({ limit: 30 }).then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  }, [user]);

  const inspect = () => {
    setInsp('loading');
    api.adminFlightInspect(route).then(({ data }) => setInsp(data)).catch(() => setInsp({ groups: [] }));
  };

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Staff access required</h1></div></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Plane className="h-6 w-6 text-accent" />Flight Comparison</h1>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate('/admin')} data-testid="flights-back">← Admin dashboard</Button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Multi-provider offer aggregation, matching &amp; economics. Providers are DEMO / PENDING_CREDENTIALS. Commission is shown for economics only and never affects ranking.</p>

        {!data ? <Skeleton className="h-24 w-full rounded-2xl" /> : (
          <>
            {/* Providers */}
            <h2 className="font-display text-lg font-semibold mb-3">Providers</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="flight-providers">
              {data.providers.map((p) => (
                <Card key={p.provider_id} className="p-4 rounded-2xl" data-testid={`fprov-${p.partner_id}`}>
                  <p className="text-sm font-semibold">{p.name}</p>
                  <Badge className="mt-1 rounded-full text-[10px] bg-amber-500/15 text-amber-700">{p.status}</Badge>
                  <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                    <p>Requests: {p.requests} · Offers: {p.offers}</p>
                    <p>Errors: {p.errors} · Timeouts: {p.timeouts} ({p.timeout_rate}%)</p>
                    <p>Avg response: {p.avg_response_ms} ms · Err rate: {p.error_rate}%</p>
                  </div>
                </Card>
              ))}
            </div>

            {/* Totals + economics */}
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <Card className="p-4 rounded-2xl" data-testid="flight-totals">
                <p className="text-sm font-semibold mb-2">Aggregation totals</p>
                <div className="text-sm space-y-1 text-muted-foreground">
                  <p>Runs: {data.totals.runs} · Offers: {data.totals.offers_total}</p>
                  <p>Matched groups: {data.totals.matched_groups} · Unmatched: {data.totals.unmatched_groups}</p>
                  <p>Stale offers: {data.totals.stale_offers}</p>
                </div>
              </Card>
              <Card className="p-4 rounded-2xl" data-testid="flight-economics">
                <p className="text-sm font-semibold mb-2">Provider economics <span className="text-[10px] text-muted-foreground">(separate from ranking)</span></p>
                {data.economics.map((e) => (
                  <div key={e.partner_id} className="flex items-center justify-between text-xs py-1 border-b border-border last:border-0">
                    <span className="capitalize font-medium">{e.partner_id}</span>
                    <span className="text-muted-foreground">{e.clicks} clicks · est {money(e.est_commission)} · conf <span className="text-emerald-600">{money(e.confirmed_commission)}</span></span>
                  </div>
                ))}
              </Card>
            </div>
          </>
        )}

        {/* Inspect */}
        <h2 className="font-display text-lg font-semibold mb-3">Inspect matched itinerary</h2>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <Input value={route.origin} onChange={(e) => setRoute({ ...route, origin: e.target.value.toUpperCase() })} className="w-24" placeholder="CCU" data-testid="inspect-origin" />
          <Input value={route.destination} onChange={(e) => setRoute({ ...route, destination: e.target.value.toUpperCase() })} className="w-24" placeholder="DEL" data-testid="inspect-dest" />
          <Input value={route.date} onChange={(e) => setRoute({ ...route, date: e.target.value })} className="w-40" data-testid="inspect-date" />
          <Button className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" onClick={inspect} data-testid="inspect-run"><Search className="h-4 w-4 mr-1.5" />Inspect</Button>
        </div>
        {insp === 'loading' ? <Skeleton className="h-24 w-full rounded-2xl" /> : insp && (
          <div className="space-y-3" data-testid="inspect-results">
            {insp.groups.map((g) => (
              <Card key={g.group_id} className="p-4 rounded-2xl">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-semibold">{g.airline_name} {g.flight_number}</span>
                  <span className="text-muted-foreground">{g.route}</span>
                  <Badge variant="outline" className="text-[10px]">{g.confidence}</Badge>
                  <span className="ml-auto text-xs">Best price: <b className="capitalize">{g.best_price_partner}</b> {money(g.best_price)} · Best value: <b className="capitalize">{g.best_value_partner}</b></span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{g.match_explanation}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {g.offers.map((o) => (
                    <span key={o.offer_id} className="text-xs bg-secondary rounded-lg px-2 py-1">{o.partner_id}: {money(o.total_price)} · {(o.baggage_kg || 0) > 0 ? `${o.baggage_kg}kg` : 'cabin'} · {o.refundable ? 'refundable' : 'non-ref'} {o.is_stale ? '· STALE' : ''}</span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
